import { useGetProductCategoriesPaginated } from "@/hooks/products/use-product-categories";
import {
  type MRT_ColumnDef,
  MRT_ToggleFiltersButton,
} from "material-react-table";
import { useMemo } from "react";

import { APIPaginatedTable } from "@/components/common/api-paginated-table";
import type { APIPaginatedTableState } from "@/components/common/api-paginated-table/states";
import type { ProductCategory } from "@/types/products/api-types/product-category-types";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { createNameFilter } from "@/lib/table-filters";

interface CategoriesTableProps {
  onCategoryClick: (category: ProductCategory) => void;
  onDeleteClick: (category: ProductCategory) => void;
}

const CategoriesTable = ({
  onCategoryClick,
  onDeleteClick,
}: CategoriesTableProps) => {
  const columns = useMemo<MRT_ColumnDef<ProductCategory>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        Cell: ({ row, renderedCellValue }) => {
          const category = row.original;
          return (
            <button
              onClick={() => onCategoryClick?.(category)}
              className="text-purple-500 hover:underline cursor-pointer"
            >
              {renderedCellValue}
            </button>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        Cell: ({ row }) => {
          const category = row.original;
          return (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onDeleteClick(category)}
            >
              <Trash2 />
            </Button>
          );
        },
      },
    ],
    [onCategoryClick, onDeleteClick]
  );

  return (
    <>
      <APIPaginatedTable
        columns={columns}
        reactQuery={useGetProductCategoriesPaginated}
        reactQueryFilters={(state: APIPaginatedTableState) => {
          const filters = createNameFilter(state.columnFilters);

          return {
            page: state.pagination.pageIndex,
            size: state.pagination.pageSize,
            ...filters,
          };
        }}
        onRenderToolbarInternalActions={({ table }) => (
          <>
            <MRT_ToggleFiltersButton table={table} />
          </>
        )}
        titleTopic="product categories"
        contentTopic="product category"
      />
    </>
  );
};

export default CategoriesTable;
